/**
 * Custom Filter System — Standalone, zero theme dependency
 * Fetch API AJAX · Dual-range price slider · Mobile bottom-sheet
 * ─────────────────────────────────────────────────────────────
 *
 * Works with Shopify Horizon theme main-collection.liquid.
 * NO changes needed to main-collection.liquid at all.
 * Selectors below target the theme's existing DOM elements.
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════
     CONFIG
     Targets Horizon theme existing elements — no template edits.

     resultsListSelector : <results-list> wraps the whole grid.
       We swap its .collection-wrapper innerHTML on every fetch.
     productGridSelector : .product-grid inside results-list,
       used only to show the loading fade.
     paginationSelector  : pagination rendered inside product-grid
       snippet — multiple fallback selectors cover theme variants.
  ═══════════════════════════════════════════════════════════ */
  var CFG = {
    resultsListSelector : 'results-list',
    productGridSelector : '.product-grid',
    paginationSelector  : '.pagination, [data-pagination], nav.pagination, .product-grid__pagination',
    resultCountSelector : '[data-cf-result-count]',
    loadingClass        : 'cf-loading',
    fetchDelay          : 300,
  };

  /* ═══════════════════════════════════════════════════════════
     STATE
  ═══════════════════════════════════════════════════════════ */
  var state = {
    params   : new URLSearchParams(window.location.search),
    fetchTimer: null,
    activePopup: null,
  };

  /* ═══════════════════════════════════════════════════════════
     UTILS
  ═══════════════════════════════════════════════════════════ */
  function fmt(n) {
    return Number(Math.round(n)).toLocaleString('en-PK');
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, delay) {
    return function () {
      var args = arguments;
      clearTimeout(state.fetchTimer);
      state.fetchTimer = setTimeout(function () { fn.apply(null, args); }, delay);
    };
  }

  /* ═══════════════════════════════════════════════════════════
     URL / PARAMS HELPERS
  ═══════════════════════════════════════════════════════════ */
  function getBaseUrl() {
    return window.location.pathname;
  }

  function buildUrl(params) {
    var str = params.toString();
    return getBaseUrl() + (str ? '?' + str : '');
  }

  function setParam(key, value) {
    if (value === '' || value === null || value === undefined) {
      state.params.delete(key);
    } else {
      state.params.set(key, value);
    }
    // Reset to page 1 when filters change
    state.params.delete('page');
  }

  function addParam(key, value) {
    // For multi-value (checkboxes) — avoid duplicates
    var existing = state.params.getAll(key);
    if (existing.indexOf(value) === -1) {
      state.params.append(key, value);
    }
    state.params.delete('page');
  }

  function removeParam(key, value) {
    if (value === undefined) {
      state.params.delete(key);
    } else {
      var remaining = state.params.getAll(key).filter(function (v) { return v !== value; });
      state.params.delete(key);
      remaining.forEach(function (v) { state.params.append(key, v); });
    }
    state.params.delete('page');
  }

  function hasParam(key, value) {
    if (value === undefined) return state.params.has(key);
    return state.params.getAll(key).indexOf(value) !== -1;
  }

  /* ═══════════════════════════════════════════════════════════
     FETCH — AJAX UPDATE
  ═══════════════════════════════════════════════════════════ */
  var debouncedFetch = debounce(doFetch, CFG.fetchDelay);

  function doFetch() {
    var url = buildUrl(state.params);

    // Push URL to browser history
    window.history.pushState({}, '', url);

    var resultsList = qs(CFG.resultsListSelector);

    // ── What we swap ──────────────────────────────────────────
    // We target ONLY .product-grid inside .collection-wrapper.
    // custom-filters snippet lives OUTSIDE .collection-wrapper
    // so it is never touched — listeners stay alive forever.
    var productGrid = resultsList
      ? qs(CFG.productGridSelector, resultsList)
      : null;

    // Fade only the product grid area while loading
    if (productGrid) productGrid.classList.add(CFG.loadingClass);

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Network error ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var parser         = new DOMParser();
        var doc            = parser.parseFromString(html, 'text/html');
        var newResultsList = doc.querySelector(CFG.resultsListSelector);

        if (productGrid && newResultsList) {
          // Grab only the new .product-grid from the fetched page
          var newProductGrid = newResultsList.querySelector(CFG.productGridSelector);
          if (newProductGrid) {
            // Replace the entire .product-grid element (outerHTML swap)
            productGrid.outerHTML = newProductGrid.outerHTML;
          }
        }

        // Update optional result count
        var countEl    = qs(CFG.resultCountSelector);
        var newCountEl = doc.querySelector(CFG.resultCountSelector);
        if (countEl && newCountEl) countEl.innerHTML = newCountEl.innerHTML;

        // Re-query after outerHTML swap (old reference is gone)
        var swappedGrid = resultsList
          ? qs(CFG.productGridSelector, resultsList)
          : null;
        if (swappedGrid) swappedGrid.classList.remove(CFG.loadingClass);

        // Re-bind pagination links inside the new grid
        bindPaginationLinks();

        // Sync filter checkmarks / chips / pills from URL state
        syncFilterUI();
      })
      .catch(function (err) {
        console.error('[CustomFilters] Fetch error:', err);
        // Re-query in case outerHTML was already swapped
        var g = resultsList ? qs(CFG.productGridSelector, resultsList) : null;
        if (g) g.classList.remove(CFG.loadingClass);
      });
  }

  /* ═══════════════════════════════════════════════════════════
     SYNC FILTER UI  (update checkmarks / pills after fetch)
  ═══════════════════════════════════════════════════════════ */
  function syncFilterUI() {
    // Checkboxes
    qsa('[data-cf-checkbox]').forEach(function (cb) {
      var key = cb.dataset.cfParam;
      var val = cb.dataset.cfValue;
      var active = hasParam(key, val);
      cb.checked = active;
      var mark = cb.parentNode.querySelector('.cf-checkmark');
      if (mark) mark.classList.toggle('cf-checkmark--on', active);
      cb.closest('.cf-value-item') && cb.closest('.cf-value-item').classList.toggle('cf-value-item--on', active);
    });

    // Radio (sort)
    qsa('[data-cf-radio]').forEach(function (r) {
      var val = r.dataset.cfValue;
      var active = state.params.get('sort_by') === val;
      r.checked = active;
      var mark = r.parentNode.querySelector('.cf-checkmark');
      if (mark) mark.classList.toggle('cf-checkmark--on', active);
      r.closest('.cf-value-item') && r.closest('.cf-value-item').classList.toggle('cf-value-item--on', active);
    });

    // Active chips
    renderActiveChips();

    // Pill active states
    syncPillStates();

    // Reset btn visibility
    var resetBtn = qs('[data-cf-reset]');
    if (resetBtn) {
      resetBtn.style.display = hasActiveFilters() ? '' : 'none';
    }
  }

  function hasActiveFilters() {
    for (var pair of state.params.entries()) {
      if (pair[0].startsWith('filter.')) return true;
    }
    return false;
  }

  function syncPillStates() {
    qsa('.cf-pill[data-filter-key]').forEach(function (pill) {
      var key = pill.dataset.filterKey;
      var active = false;
      for (var pair of state.params.entries()) {
        if (pair[0].startsWith(key)) { active = true; break; }
      }
      pill.classList.toggle('cf-pill--on', active);
    });
  }

  /* ═══════════════════════════════════════════════════════════
     ACTIVE CHIPS (desktop + mobile)
  ═══════════════════════════════════════════════════════════ */
  // In-memory chip registry — avoids storing special chars in HTML attributes.
  // Keys are sequential integers; values are {param, value} action descriptors.
  var chipRegistry = {};
  var chipCounter  = 0;

  function renderActiveChips() {
    var containers = qsa('[data-cf-chips]');
    if (!containers.length) return;

    // Reset registry for this render
    chipRegistry = {};
    chipCounter  = 0;

    var chips      = [];
    var filterDefs = getFilterDefs();

    filterDefs.forEach(function (def) {
      if (def.type === 'price_range') {
        var lo = state.params.get(def.paramMin);
        var hi = state.params.get(def.paramMax);
        if (lo || hi) {
          var idx = chipCounter++;
          chipRegistry[idx] = { type: 'price', keys: [def.paramMin, def.paramMax] };
          chips.push({ idx: idx, label: 'Price: Rs.' + fmt(lo || 0) + ' – Rs.' + fmt(hi || def.rangeMax) });
        }
      } else {
        def.values.forEach(function (v) {
          // hasParam compares against URLSearchParams which stores decoded values.
          // We must compare decoded value here too.
          if (hasParam(def.param, v.value)) {
            var idx = chipCounter++;
            // Store param + EXACT value as found in state.params
            // getAll returns decoded strings — find the one matching v.value
            var exactVal = state.params.getAll(def.param).filter(function (p) {
              return p === v.value;
            })[0] || v.value;
            chipRegistry[idx] = { type: 'list', param: def.param, value: exactVal };
            chips.push({ idx: idx, label: v.label });
          }
        });
      }
    });

    var html = '';
    chips.forEach(function (chip) {
      // Use data-chip-idx — a plain integer, no special chars
      html += '<span class="cf-chip" data-chip-idx="' + chip.idx + '">';
      // Safely render label: create text node equivalent via escaping
      html += escHtml(chip.label);
      html += '<button type="button" class="cf-chip-x" aria-label="Remove filter">&#10005;</button>';
      html += '</span>';
    });

    if (chips.length) {
      html += '<button type="button" class="cf-reset-all" data-cf-reset>Reset all</button>';
    }

    containers.forEach(function (c) {
      c.innerHTML = html;
      c.style.display = chips.length ? 'flex' : 'none';
    });

    // Bind chip X buttons — use registry index, no HTML attribute parsing
    qsa('[data-cf-chips] .cf-chip-x').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var chip = btn.closest('.cf-chip');
        if (!chip) return;
        var idx  = parseInt(chip.dataset.chipIdx, 10);
        var reg  = chipRegistry[idx];
        if (!reg) return;

        if (reg.type === 'price') {
          reg.keys.forEach(function (k) { removeParam(k); });
          // Reset price sliders visually to full range
          qsa('.cf-price-wrap').forEach(function (wrap) {
            var loEl = wrap.querySelector('.cf-thumb--lo');
            var hiEl = wrap.querySelector('.cf-thumb--hi');
            if (loEl) loEl.value = 0;
            if (hiEl) hiEl.value = hiEl.max;
            paintSlider(wrap);
            // Clear quick-select active state
            qsa('.cf-quick-btn', wrap.closest('.cf-group, .cf-popup-body') || wrap.parentNode)
              .forEach(function (b) { b.classList.remove('cf-quick-btn--on'); });
          });
        } else {
          // Remove the exact param value from state.params
          removeParam(reg.param, reg.value);
          // Also visually uncheck the matching checkbox
          qsa('[data-cf-checkbox]').forEach(function (cb) {
            if (cb.dataset.cfParam === reg.param && cb.dataset.cfValue === reg.value) {
              cb.checked = false;
              var mark = cb.parentNode.querySelector('.cf-checkmark');
              if (mark) mark.classList.remove('cf-checkmark--on');
              var item = cb.closest('.cf-value-item');
              if (item) item.classList.remove('cf-value-item--on');
            }
          });
        }

        syncFilterUI();
        debouncedFetch();
      });
    });

    // Bind reset buttons
    qsa('[data-cf-reset]').forEach(function (btn) {
      if (btn._cfResetBound) return; btn._cfResetBound = true;
      btn.addEventListener('click', resetAllFilters);
    });
  }

  // Safe HTML entity escaper for chip labels
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getFilterDefs() {
    // Read filter definitions embedded by Liquid in the DOM
    var el = qs('[data-cf-filter-defs]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) { return []; }
  }

  function resetAllFilters() {
    for (var key of Array.from(state.params.keys())) {
      if (key.startsWith('filter.')) state.params.delete(key);
    }
    syncFilterUI();
    // Reset price sliders visually
    qsa('.cf-price-wrap').forEach(function (wrap) {
      var lo = wrap.querySelector('.cf-thumb--lo');
      var hi = wrap.querySelector('.cf-thumb--hi');
      if (lo) lo.value = lo.min;
      if (hi) hi.value = hi.max;
      paintSlider(wrap);
    });
    debouncedFetch();
  }

  /* ═══════════════════════════════════════════════════════════
     PRICE SLIDER
  ═══════════════════════════════════════════════════════════ */
  function initAllSliders() {
    qsa('.cf-price-wrap:not([data-ready])').forEach(initSlider);
  }

  function paintSlider(wrap) {
    var lo     = wrap.querySelector('.cf-thumb--lo');
    var hi     = wrap.querySelector('.cf-thumb--hi');
    var fill   = wrap.querySelector('.cf-track-fill');
    var loLbl  = wrap.querySelector('.cf-lo-lbl');
    var hiLbl  = wrap.querySelector('.cf-hi-lbl');
    // range input values are already in display units (Rs.)
    // absMax is also in display units (set correctly in initSlider)
    var absMax = parseFloat(wrap.dataset.rangeMax) || 100000;

    if (!lo || !hi) return;
    var v0 = parseFloat(lo.value);
    var v1 = parseFloat(hi.value);
    var p0 = (v0 / absMax) * 100;
    var p1 = (v1 / absMax) * 100;
    if (fill) { fill.style.left = p0 + '%'; fill.style.width = (p1 - p0) + '%'; }
    if (loLbl) loLbl.textContent = fmt(v0);
    if (hiLbl) hiLbl.textContent = fmt(v1);
  }

  function initSlider(wrap) {
    if (wrap.dataset.ready) return;
    wrap.setAttribute('data-ready', '1');

    // ── Shopify price fix ──────────────────────────────────────
    // Shopify's filter.range_max is in CENTS (e.g. Rs.7,810 = 781000).
    // The range input max attr in the liquid is set from range_max (cents).
    // We divide by 100 here to work in display units (Rs.) throughout.
    var rawMax  = parseFloat(wrap.dataset.rangeMax) || 10000000;
    var absMax  = rawMax / 100;   // convert cents → display currency

    // Update the range inputs' max to display units so dragging is correct
    var paramLo = wrap.dataset.paramLo;
    var paramHi = wrap.dataset.paramHi;
    var lo      = wrap.querySelector('.cf-thumb--lo');
    var hi      = wrap.querySelector('.cf-thumb--hi');

    if (!lo || !hi) return;

    lo.max = absMax;
    hi.max = absMax;

    // Also update data-range-max so paintSlider uses display units
    wrap.dataset.rangeMax = absMax;

    // Restore from URL params (Shopify stores prices in display units in URL)
    var savedLo = parseFloat(state.params.get(paramLo));
    var savedHi = parseFloat(state.params.get(paramHi));
    lo.value = (savedLo > 0)       ? savedLo : 0;
    hi.value = (savedHi > 0 && savedHi <= absMax) ? savedHi : absMax;

    paintSlider(wrap);

    lo.addEventListener('input', function () {
      if (parseFloat(lo.value) > parseFloat(hi.value)) lo.value = hi.value;
      paintSlider(wrap);
    });
    hi.addEventListener('input', function () {
      if (parseFloat(hi.value) < parseFloat(lo.value)) hi.value = lo.value;
      paintSlider(wrap);
    });

    function commitSlider() {
      var v0 = parseFloat(lo.value);
      var v1 = parseFloat(hi.value);
      // Only set params if not at the full range
      if (v0 <= 0 && v1 >= absMax) {
        removeParam(paramLo);
        removeParam(paramHi);
      } else {
        setParam(paramLo, v0 > 0 ? v0 : '');
        setParam(paramHi, v1 < absMax ? v1 : '');
      }
      syncFilterUI();
      debouncedFetch();
    }

    lo.addEventListener('change', commitSlider);
    hi.addEventListener('change', commitSlider);

    // Quick-select buttons — click active button again to deselect
    var scope = wrap.closest('.cf-group, .cf-popup-body') || wrap.parentNode;
    qsa('.cf-quick-btn', scope).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isActive = btn.classList.contains('cf-quick-btn--on');

        // Always clear all active states first
        qsa('.cf-quick-btn', scope).forEach(function (b) { b.classList.remove('cf-quick-btn--on'); });

        if (isActive) {
          // Second click on same button = deselect, reset to full range
          lo.value = 0;
          hi.value = absMax;
          removeParam(paramLo);
          removeParam(paramHi);
        } else {
          // First click = select this range
          btn.classList.add('cf-quick-btn--on');
          var newLo = parseFloat(btn.dataset.lo);
          var newHi = Math.min(parseFloat(btn.dataset.hi), absMax);
          lo.value = newLo;
          hi.value = newHi;
          setParam(paramLo, newLo > 0 ? newLo : '');
          setParam(paramHi, newHi < absMax ? newHi : '');
        }

        paintSlider(wrap);
        syncFilterUI();
        debouncedFetch();
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     CHECKBOX / RADIO FILTERS
  ═══════════════════════════════════════════════════════════ */
  function initCheckboxes() {
    qsa('[data-cf-checkbox]').forEach(function (cb) {
      if (cb._cfBound) return; cb._cfBound = true;

      // Set initial checked state from URL
      var key = cb.dataset.cfParam;
      var val = cb.dataset.cfValue;
      cb.checked = hasParam(key, val);
      var mark = cb.parentNode.querySelector('.cf-checkmark');
      if (mark) mark.classList.toggle('cf-checkmark--on', cb.checked);
      cb.closest('.cf-value-item') && cb.closest('.cf-value-item').classList.toggle('cf-value-item--on', cb.checked);

      cb.addEventListener('change', function () {
        if (cb.checked) {
          addParam(key, val);
        } else {
          removeParam(key, val);
        }
        var mark2 = cb.parentNode.querySelector('.cf-checkmark');
        if (mark2) mark2.classList.toggle('cf-checkmark--on', cb.checked);
        cb.closest('.cf-value-item') && cb.closest('.cf-value-item').classList.toggle('cf-value-item--on', cb.checked);
        syncFilterUI();
        debouncedFetch();
      });
    });
  }

  function initRadios() {
    qsa('[data-cf-radio]').forEach(function (r) {
      if (r._cfBound) return; r._cfBound = true;

      var val = r.dataset.cfValue;
      var currentSort = state.params.get('sort_by') || '';
      r.checked = (val === currentSort) || (val === 'manual' && !currentSort);
      var mark = r.parentNode.querySelector('.cf-checkmark');
      if (mark) mark.classList.toggle('cf-checkmark--on', r.checked);
      r.closest('.cf-value-item') && r.closest('.cf-value-item').classList.toggle('cf-value-item--on', r.checked);

      r.addEventListener('change', function () {
        if (r.checked) {
          setParam('sort_by', val);
          // Update all radio marks in same group
          qsa('[data-cf-radio]').forEach(function (other) {
            var isActive = other.dataset.cfValue === val;
            other.checked = isActive;
            var m = other.parentNode.querySelector('.cf-checkmark');
            if (m) m.classList.toggle('cf-checkmark--on', isActive);
            other.closest('.cf-value-item') && other.closest('.cf-value-item').classList.toggle('cf-value-item--on', isActive);
          });
          syncFilterUI();
          debouncedFetch();
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     MOBILE POPUP / BOTTOM-SHEET
  ═══════════════════════════════════════════════════════════ */
  function initPopups() {
    var overlay = qs('#cf-overlay');

    function closeAll() {
      qsa('.cf-popup.cf-popup--open').forEach(function (p) {
        p.classList.remove('cf-popup--open');
        p.setAttribute('aria-hidden', 'true');
      });
      qsa('.cf-pill.cf-pill--open').forEach(function (p) {
        p.classList.remove('cf-pill--open');
        p.setAttribute('aria-expanded', 'false');
      });
      if (overlay) overlay.classList.remove('cf-overlay--on');
      document.body.classList.remove('cf-no-scroll');
      state.activePopup = null;
    }

    function openPopup(id, pill) {
      closeAll();
      var popup = qs('#' + id);
      if (!popup) return;

      // ── Sync checkbox visual state before opening ──────────
      // Re-apply checked/unchecked marks from current URL state
      // so the popup always reflects what is actually active.
      qsa('[data-cf-checkbox]', popup).forEach(function (cb) {
        var active = hasParam(cb.dataset.cfParam, cb.dataset.cfValue);
        cb.checked = active;
        var mark = cb.parentNode.querySelector('.cf-checkmark');
        if (mark) mark.classList.toggle('cf-checkmark--on', active);
        var item = cb.closest('.cf-value-item');
        if (item) item.classList.toggle('cf-value-item--on', active);
      });
      qsa('[data-cf-radio]', popup).forEach(function (r) {
        var active = state.params.get('sort_by') === r.dataset.cfValue;
        r.checked = active;
        var mark = r.parentNode.querySelector('.cf-checkmark');
        if (mark) mark.classList.toggle('cf-checkmark--on', active);
        var item = r.closest('.cf-value-item');
        if (item) item.classList.toggle('cf-value-item--on', active);
      });

      popup.classList.add('cf-popup--open');
      popup.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('cf-overlay--on');
      document.body.classList.add('cf-no-scroll');
      if (pill) { pill.classList.add('cf-pill--open'); pill.setAttribute('aria-expanded', 'true'); }
      state.activePopup = id;

      // Init price slider inside popup (only once per popup)
      qsa('.cf-price-wrap', popup).forEach(initSlider);
    }

    // ── Pills ─────────────────────────────────────────────────
    qsa('.cf-pill[data-popup]').forEach(function (pill) {
      if (pill._cfBound) return; pill._cfBound = true;
      pill.addEventListener('click', function () {
        var id = pill.dataset.popup;
        if (state.activePopup === id) closeAll();
        else openPopup(id, pill);
      });
    });

    // ── X close buttons ───────────────────────────────────────
    qsa('.cf-popup-close[data-close]').forEach(function (btn) {
      if (btn._cfBound) return; btn._cfBound = true;
      btn.addEventListener('click', closeAll);
    });

    // ── Apply buttons — fire fetch THEN close ─────────────────
    // Apply buttons have data-apply (not data-close) so they
    // trigger the fetch and then close the popup.
    qsa('.cf-apply-btn[data-apply]').forEach(function (btn) {
      if (btn._cfBound) return; btn._cfBound = true;
      btn.addEventListener('click', function () {
        closeAll();
        doFetch();
      });
    });

    // ── Overlay click → close ─────────────────────────────────
    if (overlay && !overlay._cfBound) {
      overlay._cfBound = true;
      overlay.addEventListener('click', closeAll);
    }

    // ── ESC ───────────────────────────────────────────────────
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.activePopup) closeAll();
    });
  }

  /* ═══════════════════════════════════════════════════════════
     PAGINATION (re-bind after fetch)
  ═══════════════════════════════════════════════════════════ */
  function bindPaginationLinks() {
    // Always search inside .product-grid (which was just swapped in)
    var resultsList = qs(CFG.resultsListSelector);
    var grid        = resultsList ? qs(CFG.productGridSelector, resultsList) : null;
    var scope       = grid || resultsList || document;

    // Pagination links — try named selectors first, then any page= link
    var paginationLinks = qsa(CFG.paginationSelector + ' a', scope);
    if (!paginationLinks.length) {
      paginationLinks = qsa('a[href*="page="]', scope);
    }

    paginationLinks.forEach(function (link) {
      if (link._cfBound) return; link._cfBound = true;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var href = new URL(link.href);
        var page = href.searchParams.get('page');
        if (page) state.params.set('page', page);
        else state.params.delete('page');
        doFetch();
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     BROWSER BACK/FORWARD
  ═══════════════════════════════════════════════════════════ */
  window.addEventListener('popstate', function () {
    state.params = new URLSearchParams(window.location.search);
    doFetch();
    syncFilterUI();
  });

  /* ═══════════════════════════════════════════════════════════
     BOOT
  ═══════════════════════════════════════════════════════════ */
  function boot() {
    initAllSliders();
    initCheckboxes();
    initRadios();
    initPopups();
    bindPaginationLinks();
    renderActiveChips();
    syncPillStates();

    // Hide reset btn if no active filters
    var resetBtn = qs('[data-cf-reset]');
    if (resetBtn) resetBtn.style.display = hasActiveFilters() ? '' : 'none';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();